import type { Column, Row, Table } from '@tanstack/react-table';
import { defaultColumnSizing } from '@tanstack/react-table';
import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type {
  CSSProperties,
  HTMLAttributes,
  ReactNode,
  Ref,
  RefObject,
} from 'react';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Loader } from 'semantic-ui-react';

import { LoadMoreFooter } from '@components/load-more-footer';
import type {
  InfiniteScrollOptions,
  ScrollToIndexOptions,
  VirtualWindowOptions,
} from '@components/virtual-list';
import { resetScrollToTop, subscribeToScrollReset } from '@utils/scroll-reset';

import { TableBody, TableEmptyState, TableHead } from './table-primitives';
import { TableScrollbarProxy, TableScrollScrims } from './table-scroll-chrome';
import type {
  ColumnDef,
  DataTableInstance,
  NoDataContent,
  TableRowOptions,
  VirtualRowsHandle,
} from './types';
import {
  getCanvasWidth,
  useHorizontalTableScroll,
} from './use-horizontal-table-scroll';
import { useStickyTableHeader } from './use-sticky-table-header';
import { useTableResize } from './use-table-resize';
import s from './table-view.scss';

// How long a fetch runs before the loading overlay appears, and how long a reset
// stays armed to skip that wait when no fetch follows it.
const LOADING_OVERLAY_DELAY_MS = 300;

export interface TableViewProps<TData> extends TableRowOptions<TData> {
  /**
   * Accessible name for the table.
   */
  'aria-label'?: string;
  /**
   * Extra class applied to the table root.
   */
  className?: string;
  /**
   * Whether an active filter is applied, switching the empty state to its
   * filtered variant.
   */
  filtering?: boolean;
  /**
   * Fetches more rows as the windowed tail is reached; enables virtualization.
   */
  infinite?: InfiniteScrollOptions;
  /**
   * Ref populated with the table instance, augmented with an imperative
   * `scrollToIndex` for the windowed body.
   */
  instanceRef?: Ref<DataTableInstance<TData>>;
  /**
   * Shows a loading overlay over the current contents.
   */
  loading?: boolean;
  /**
   * Empty-state content: props for the default placeholder, a node to render
   * as-is, or a render function given the current empty-state context.
   */
  noData?: NoDataContent;
  /**
   * Whether columns can be resized by dragging their header grips — the master
   * switch a column's own `enableResizing` can still opt out of.
   */
  resizable?: boolean;
  /**
   * Whether the header sticks, or a ref to the element it sticks beneath.
   */
  stickyHeader?: boolean | RefObject<HTMLElement>;
  /**
   * The built TanStack table instance whose current row model is rendered.
   */
  table: Table<TData>;
  /**
   * Toolbar rendered above the table (e.g. a pager).
   */
  toolbar?: ReactNode;
  /**
   * Whether the body is windowed, optionally with window tuning options.
   */
  virtualized?: boolean | VirtualWindowOptions;
}

/**
 * The consumer-facing props shared by the ClientDataTable and ServerDataTable
 * wrappers: the presentational surface plus the column/row inputs TableView's
 * own `table` instance stands in for.
 */
export interface DataTableProps<TData> extends Pick<
  TableViewProps<TData>,
  | 'aria-label'
  | 'className'
  | 'instanceRef'
  | 'isRowActive'
  | 'loading'
  | 'noData'
  | 'onRowClick'
  | 'resizable'
  | 'rowClassName'
  | 'stickyHeader'
  | 'virtualized'
> {
  /**
   * TanStack column definitions, extended with our column `meta`.
   */
  columns: ColumnDef<TData>[];
  /**
   * The row set to render.
   */
  data: TData[];
  /**
   * Renders extra content beneath a row while it is expanded.
   * @deprecated Transitional slot pending a first-class expansion API.
   */
  expandedRow?: (row: Row<TData>) => ReactNode;
  /**
   * Whether shift-click extends the sort across multiple columns; on by
   * default, matching the legacy table.
   */
  multiSort?: boolean;
}

const getColumnVars = <TData,>(
  column: Column<TData>,
  resizeWidth?: number,
): CSSProperties => {
  const { align, allowOverflow, fixedWidth, wrap } =
    column.columnDef.meta ?? {};
  const isFixed = fixedWidth !== undefined;
  const width = resizeWidth ?? column.getSize();

  // Custom properties, not classes, so the stylesheet reads them at zero
  // specificity and `className`/`headerClassName` can still override either side.
  // React drops the undefined entries, leaving the stylesheet's own fallbacks.
  return {
    '--table-cell-align': align,
    '--table-cell-overflow': allowOverflow ? 'visible' : undefined,
    '--table-cell-white-space': wrap ? 'normal' : undefined,
    // TanStack sizes are pixels, so a column's size sets both the width it holds
    // and its share of any surplus space.
    '--table-col-basis': isFixed ? `${fixedWidth}rem` : `${width}px`,
    '--table-col-grow': isFixed ? 0 : width,
    '--table-col-max': isFixed ? `${fixedWidth}rem` : undefined,
    // The header pads its trailing edge only to reserve room for the sort icon,
    // which sits on the leading side.
    '--table-th-pad-end': align === 'end' ? '0' : undefined,
  } as CSSProperties;
};

/**
 * The shared presentational core: it renders the toolbar, header (with sort
 * affordances and description tooltips), body (plain or windowed), empty state,
 * and loading overlay for a pre-built table instance, leaving state ownership to
 * the ClientDataTable and ServerDataTable wrappers.
 */
export const TableView = <TData,>({
  'aria-label': ariaLabel,
  className,
  expandedRow,
  filtering = false,
  infinite,
  instanceRef,
  isRowActive,
  loading = false,
  noData,
  onRowClick,
  resizable,
  rowClassName,
  stickyHeader,
  table,
  toolbar,
  virtualized,
}: TableViewProps<TData>) => {
  useStyles(s);

  const bodyCanvasRef = useRef<HTMLDivElement>(null);
  const bodyViewportRef = useRef<HTMLDivElement>(null);
  const headerCanvasRef = useRef<HTMLDivElement>(null);
  const proxyGhostRef = useRef<HTMLDivElement>(null);
  const proxyRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const virtualHandleRef = useRef<VirtualRowsHandle>(null);

  // Delegate through the prototype rather than spreading: TanStack reassigns
  // `table.options` every render, so a copy would freeze it at the first.
  useImperativeHandle(
    instanceRef,
    () =>
      Object.assign(Object.create(table) as Table<TData>, {
        scrollToIndex: (index: number, options?: ScrollToIndexOptions) =>
          virtualHandleRef.current?.scrollToIndex(index, options),
      }),
    [table],
  );

  const { isColumnHeaderPinned, stickyInsetTop } = useStickyTableHeader({
    sentinelRef,
    stickyHeader,
    tableRef: rootRef,
  });

  // Serialized rather than compared by identity: a consumer feeding `sort` a
  // fresh array each render would otherwise re-scroll on every render.
  const sortKey = table
    .getState()
    .sorting.map(({ desc, id }) => `${id}:${desc}`)
    .join();
  const previousSortKeyRef = useRef(sortKey);

  // Gated on sticky: otherwise a table scrolled past has no reachable header
  // that could have triggered this.
  useEffect(() => {
    if (previousSortKeyRef.current === sortKey) return;
    previousSortKeyRef.current = sortKey;

    if (!stickyHeader) return;

    const root = rootRef.current;
    if (root && root.getBoundingClientRect().top < stickyInsetTop) {
      resetScrollToTop(root);
    }
  }, [sortKey, stickyHeader, stickyInsetTop]);

  const visibleLeafColumns = table.getVisibleLeafColumns();

  const resizeColumns = visibleLeafColumns.map(column => ({
    fixedWidth: column.columnDef.meta?.fixedWidth,
    id: column.id,
    maxSize: column.columnDef.maxSize,
    // TanStack backfills its sizing defaults into every column def, so a column
    // declaring no minimum arrives with 20px and would sink the resize floor. 20 is
    // therefore reserved: a column asking for it explicitly is re-floored at 70.
    minSize:
      column.columnDef.minSize === defaultColumnSizing.minSize
        ? undefined
        : column.columnDef.minSize,
    resizable: column.columnDef.enableResizing,
    size: column.getSize(),
  }));

  const { columnWidths, getResizeHandler, resizingColumnId } = useTableResize({
    columns: resizeColumns,
    containerRef: bodyCanvasRef,
    enabled: Boolean(resizable),
  });

  // Cells are shrink-0 over a definite basis, so the resolved widths are the canvas
  // the rowgroups stretch to.
  const canvasWidth = getCanvasWidth(
    visibleLeafColumns.map(column => ({
      fixedWidth: column.columnDef.meta?.fixedWidth,
      width: columnWidths[column.id] ?? column.getSize(),
    })),
  );

  useHorizontalTableScroll({
    bodyCanvasRef,
    bodyViewportRef,
    headerCanvasRef,
    measureKey: canvasWidth,
    proxyGhostRef,
    proxyRef,
    rootRef,
  });

  // A grip needs a column to its right to take width from; the rightmost has none.
  const resizableColumnIds = resizable
    ? resizeColumns
        .filter(
          column =>
            column.fixedWidth === undefined && (column.resizable ?? true),
        )
        .map(column => column.id)
    : [];

  const resizeHandleColumnIds = new Set(resizableColumnIds.slice(0, -1));
  const resizableColumnIdSet = new Set(resizableColumnIds);

  const getColumnStyle = (column: Column<TData>): CSSProperties =>
    getColumnVars(column, columnWidths[column.id]);

  const getResizeHandleProps = (
    columnId: string,
  ):
    | Pick<HTMLAttributes<HTMLDivElement>, 'onClick' | 'onMouseDown'>
    | undefined =>
    resizeHandleColumnIds.has(columnId)
      ? {
          onClick: event => event.stopPropagation(),
          onMouseDown: getResizeHandler(columnId),
        }
      : undefined;

  const { rows } = table.getRowModel();

  // The empty placeholder is a self-contained design, so the frame is dropped
  // while it shows — but not mid-load, where it holds the transient state.
  const isShowingNoData = rows.length === 0 && !loading && !infinite?.isLoading;

  // Seeded so a table mounting mid-fetch dims at once.
  const [isDelayElapsed, setIsDelayElapsed] = useState(loading);

  useEffect(() => {
    if (!loading) {
      setIsDelayElapsed(false);
      return undefined;
    }

    const timer = setTimeout(
      () => setIsDelayElapsed(true),
      LOADING_OVERLAY_DELAY_MS,
    );

    return () => clearTimeout(timer);
  }, [loading]);

  const [isCoveringReset, setIsCoveringReset] = useState(false);
  const wasLoadingRef = useRef(loading);

  useEffect(() => subscribeToScrollReset(() => setIsCoveringReset(true)), []);

  // Trailing edge, not a fixed window: a reset lands a render before `loading`,
  // so a window would lapse mid-fetch. The timer only bounds a reset with no fetch.
  useEffect(() => {
    const hasSettled = wasLoadingRef.current && !loading;
    wasLoadingRef.current = loading;

    if (hasSettled) {
      setIsCoveringReset(false);
      return undefined;
    }

    if (!isCoveringReset || loading) return undefined;

    const timer = setTimeout(
      () => setIsCoveringReset(false),
      LOADING_OVERLAY_DELAY_MS,
    );

    return () => clearTimeout(timer);
  }, [isCoveringReset, loading]);

  // A reset dims at once; the delay is for quiet refetches, which move nothing.
  const isShowingLoading = loading && (isCoveringReset || isDelayElapsed);

  // The fetch, not the scrim, which is delayed and aria-hidden.
  const isBusy = loading || Boolean(infinite?.isLoading);

  return (
    <div
      ref={rootRef}
      className={classNames(
        s.root,
        stickyHeader && s.sticky,
        resizingColumnId && s.resizing,
        isShowingNoData && s.empty,
        className,
      )}
      data-table-view
    >
      {toolbar ? (
        <div
          className={classNames(
            s.paginationTop,
            isColumnHeaderPinned && s.pinned,
          )}
        >
          {toolbar}
        </div>
      ) : null}

      <div
        aria-busy={isBusy}
        aria-label={ariaLabel}
        className={s.table}
        role="table"
        style={{ '--table-min-width': canvasWidth } as CSSProperties}
      >
        <div
          ref={sentinelRef}
          aria-hidden
          data-testid="table-sticky-sentinel"
        />

        <div className={s.headerViewport} role="presentation">
          <div
            ref={headerCanvasRef}
            className={s.headerCanvas}
            role="presentation"
          >
            <TableHead
              getColumnStyle={getColumnStyle}
              getResizeHandleProps={getResizeHandleProps}
              headerGroups={table.getHeaderGroups()}
              isColumnResizable={columnId => resizableColumnIdSet.has(columnId)}
              isPinned={isColumnHeaderPinned}
            />
          </div>
        </div>

        <div
          ref={bodyViewportRef}
          className={s.bodyViewport}
          role="presentation"
        >
          <div ref={bodyCanvasRef} className={s.bodyCanvas} role="presentation">
            {rows.length > 0 ? (
              <TableBody
                expandedRow={expandedRow}
                getColumnStyle={getColumnStyle}
                infinite={infinite}
                isRowActive={isRowActive}
                onRowClick={onRowClick}
                rootRef={rootRef}
                rowClassName={rowClassName}
                rows={rows}
                stickyInsetTop={stickyInsetTop}
                virtualHandleRef={virtualHandleRef}
                virtualized={virtualized}
              />
            ) : null}
          </div>
        </div>

        <TableScrollScrims />
      </div>

      {isShowingNoData ? (
        <TableEmptyState filtering={filtering} noData={noData} />
      ) : null}

      {infinite && !isShowingNoData ? (
        <LoadMoreFooter
          isLoading={infinite.isLoading ?? false}
          message={infinite.loadingMessage}
        />
      ) : null}

      <TableScrollbarProxy ghostRef={proxyGhostRef} scrollerRef={proxyRef} />

      {isShowingLoading ? (
        <div aria-hidden className={s.loading}>
          <div className={s.loadingSpinner}>
            <Loader active size="huge" />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TableView;
